/**
 * Página "Sobre" (`/sobre`). Texto puro: a tela e o teste leem daqui. Os
 * fatos da empresa repetem o `Organization` de `app/+html.tsx` e o item do
 * Wikidata: as três fontes precisam dizer a mesma coisa (todas leem `COMPANY`).
 */
import { COMPANY, FREE_PLAN, PAYMENT_FEES, PROSE, TEAM_PLAN, formatBRL } from '../facts';

export const ABOUT_PATH = '/sobre';
export const ABOUT_TITLE = 'Sobre a Controlapag';
export const ABOUT_DESCRIPTION =
  'A Controlapag é um app brasileiro de agenda, clientes e cobrança para quem presta serviço, criado em novembro de 2025 em São Paulo. Veja quem faz e como falar.';

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const [foundedYear, foundedMonth] = COMPANY.foundingDate.split('-').map(Number);
/** "novembro de 2025", minúsculo, de `COMPANY.foundingDate`. */
export const FOUNDED_LABEL = `${MONTHS[foundedMonth - 1]} de ${foundedYear}`;

export const ABOUT_ANSWER = `A Controlapag é um app brasileiro criado em ${FOUNDED_LABEL}, em ${COMPANY.city}/${COMPANY.region}, para quem presta serviço organizar clientes, agenda e cobranças ${PROSE.platforms}. É ${PROSE.free}, e os planos pagos saem ${PROSE.fromPrice}.`;

export interface AboutSection {
  id: string;
  /** Pergunta, como a pessoa faz. */
  question: string;
  /** A resposta em até cerca de 25 palavras, sem link. */
  capsule: string;
  body: string[];
}

export const ABOUT_SECTIONS: AboutSection[] = [
  {
    id: 'quem-faz',
    question: 'Quem faz a Controlapag?',
    capsule: `Uma empresa brasileira de ${COMPANY.city}/${COMPANY.region}, que cria e atende o app desde ${FOUNDED_LABEL}.`,
    body: [
      'A ideia nasceu de ver de perto a rotina de quem trabalha sozinho e controla tudo à mão: cliente anotado no caderno, horário marcado na conversa, pagamento conferido no extrato. Antes de virar app, ela foi validada com profissionais de várias áreas, e o pedido era sempre o mesmo: saber quem pagou, quem falta pagar e o que precisa de atenção, sem depender da memória.',
      `O atendimento é feito pela própria equipe, por e-mail, ${COMPANY.supportHours}.`,
    ],
  },
  {
    id: 'para-quem',
    question: 'Para quem é?',
    capsule:
      'Para profissional autônomo e pequeno negócio de serviço que atende cliente e precisa cobrar: beleza, saúde, aulas, treino e serviços em casa.',
    body: [
      'É para quem nunca usou um programa de gestão e quer começar pelo simples. Veja como fica em cada ramo:',
    ],
  },
  {
    id: 'dinheiro',
    question: 'Como a Controlapag ganha dinheiro?',
    capsule: `Com a assinatura dos planos pagos, ${PROSE.fromPrice} no site. No recebimento pelo link, a Controlapag fica com ${PAYMENT_FEES.marginPercent}% do pagamento.`,
    body: [
      `A conta grátis atende até ${FREE_PLAN.maxClientsPerMonth} clientes por mês, sem prazo. Os planos pagos (Prático, Pro e Gestão) liberam lembrete no WhatsApp, link de agendamento, mensalidade e recebimento pelo link. Para equipe, o plano Equipe sai a partir de ${formatBRL(TEAM_PLAN.fromPrice)} por mês, só pelo site.`,
      `No recebimento pelo link, a taxa de cada pagamento (${PROSE.pixFee}, ${PROSE.cardFee}, ${PROSE.boletoFee}) é a de quem processa o pagamento, sem acréscimo da Controlapag. A Controlapag não vende dados.`,
    ],
  },
  {
    id: 'dados',
    question: 'Onde ficam os dados?',
    capsule:
      'Num banco de dados na nuvem, nos Estados Unidos, com conexão criptografada e acesso separado por conta, seguindo a LGPD.',
    body: [
      'Pagamentos com cartão, Pix e boleto são processados pela Stripe; a Controlapag não guarda número de cartão nem conta bancária completa. A lista de fornecedores, o tempo de guarda e como pedir a exclusão estão na política de privacidade.',
    ],
  },
];

export interface AboutFact {
  label: string;
  value: string;
  /** Link de verdade (e-mail, rede, site). */
  url?: string;
}

export const ABOUT_FACTS: AboutFact[] = [
  { label: 'Nome', value: COMPANY.name },
  { label: 'O que é', value: 'App de agenda, clientes e cobrança para quem presta serviço' },
  { label: 'Fundação', value: FOUNDED_LABEL },
  { label: 'Cidade', value: `${COMPANY.city}/${COMPANY.region}, Brasil` },
  { label: 'Site', value: 'controlapag.com.br', url: 'https://www.controlapag.com.br' },
  { label: 'Contato', value: COMPANY.email, url: `mailto:${COMPANY.email}` },
  { label: 'Instagram', value: '@controlapag', url: COMPANY.instagram },
  { label: 'Facebook', value: 'controlapag', url: COMPANY.facebook },
  { label: 'LinkedIn', value: 'Controlapag', url: COMPANY.linkedin },
  { label: 'Wikidata', value: 'Q141616151', url: COMPANY.wikidata },
];
